import ScopeIllustration from '../../../components/ui/ScopeIllustration';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { loginUrl } from '../../../utils/loginRedirect';
import { estimateSessionDuration } from '../../../utils/format';
import useAuthStore from '../../../store/authStore';
import usePageTitle from '../../../hooks/usePageTitle';
import useQuizStore from '../../../store/quizStore';
import useDashboardPrefs from '../../../hooks/useDashboardPrefs';
import { pingFeature } from '../../../hooks/useFeaturePing';
import WelcomeBackShortcuts, { WelcomeBackGate, welcomeHeading } from './WelcomeBack';
import useNavStore from '../../../store/navStore';
import useCustomStore from '../../../store/customStore';
import api from '../../../services/api';
import ThemeToggle from '../../../components/ui/ThemeToggle';
import SubscriptionWall from '../../../components/subscription/SubscriptionWall';
import SubscriptionModal from '../../../components/stripe/SubscriptionModal';
import UnlockFab from '../../../components/stripe/UnlockFab';
import ShareExtendRequest from '../../../components/share/ShareExtendRequest';
import { CheckSquare, FileText, Brain, BarChart3, BookOpen, ClipboardList, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Clock, ArrowRight, Eye, ThumbsUp, Sparkles, Filter, Gift, AlertTriangle, Lock, PenLine } from 'lucide-react';
import TabRedaction from './TabRedaction';
import TabNotions, { isBuyable } from './TabNotions';
import ArcNav from '../../../components/ui/ArcNav';
import ParcoursSwitch, { filtrerParParcours, compterParParcours, normaliserParcours } from '../../../components/ui/ParcoursSwitch';
import { setParcoursActif } from '../../../services/parcoursActif';
import DynamicIcon from '../../../components/ui/DynamicIcon';
import Quiz from '../Quiz';
import TabQuiz from './TabQuiz';
import TabExamen from './TabExamen';
import TabEntrainement from './TabEntrainement';
import GuidedLearning from '../GuidedLearning';
import GuidedNextStep, { GuidedNextStepTeaser } from '../../../components/quiz/GuidedNextStep';
import TabResultats from './TabResultats';
import { pickNextSubject, programmeCoverage } from './nextSubject';
import ExamDatePill from '../../../components/ui/ExamDatePill';
import ParrainageEncart from './ParrainageEncart';
import NotionsAChoisirEncart from './NotionsAChoisirEncart';
import MesThematiquesHub from './MesThematiquesHub';

const LAST_SCOPE_KEY = 'monquiz_last_scope';

export const TABS = [
    { id: 'quiz', label: 'S’entraîner', icon: CheckSquare },
    { id: 'examen', label: 'Mélange', icon: FileText },
    { id: 'entrainement', label: 'Progression', icon: Brain },
    { id: 'redaction', label: 'Rédaction', icon: PenLine }, // réponses rédigées : carte du dock affichée seulement si la thématique en a
    { id: 'notions', label: 'Notions', icon: BookOpen }, // notions à la carte (migration 183) : carte affichée seulement s'il y en a
    { id: 'resultats', label: 'Mes résultats', icon: BarChart3 },
];

const TAB_GENERATIONS = { id: 'generations', label: 'Mes générations', icon: Sparkles };

export const SMART_MODES = [
    { id: 'mixed', label: 'Quiz intelligent', desc: 'Mix optimal basé sur votre profil.' },
    { id: 'weaknesses', label: 'Mes faiblesses', desc: 'Ciblez vos notions les plus faibles.' },
    { id: 'revision', label: 'Révision', desc: 'Notions pas vues depuis longtemps.' },
    { id: 'consolidation', label: 'Consolidation', desc: 'Passez au niveau supérieur.' },
];

export const ERROR_MODES = [
    { id: 'never', label: 'Jamais réussies', desc: 'Questions auxquelles vous n\'avez jamais répondu correctement.', color: '#dc3545', group: 'performance' },
    { id: 'sometimes', label: 'Instables', desc: 'Questions parfois réussies, parfois ratées.', color: '#e67e22', group: 'performance' },
    { id: 'recent', label: 'Erreurs récentes', desc: 'Erreurs dans vos 3 dernières tentatives.', color: '#ffc107', group: 'recent' },
];

export const COLORS = { good: '#28a745', ok: '#e67e22', bad: '#dc3545' };
export const getColor = (pct) => pct >= 75 ? COLORS.good : pct >= 50 ? COLORS.ok : COLORS.bad;

export const NB_QUESTIONS_OPTIONS = [10, 20, 30];

export default function ScopeLanding() {
    const { scope } = useParams();
    // Chaque concours a son propre etat de chargement et ses propres resultats.
    // Sinon une reponse tardive du precedent peut marquer le nouveau comme charge.
    return <ScopeContent key={scope} />;
}

function ScopeContent() {
    const { scope } = useParams();
    const navigate = useNavigate();

    // thematique-personnalisable is a billing-only scope — redirect to hub
    useEffect(() => {
        if (scope === 'thematique-personnalisable') {
            navigate('/compte/quiz/mes-thematiques', { replace: true });
        }
    }, [scope]);

    // Detect scope type (set after data loads, from scope.content_source)
    const [isUserThematique, setIsUserThematique] = useState(false);
    const [isPartnerScope, setIsPartnerScope] = useState(false);
    const isMaThematique = isUserThematique; // alias for backward compat in child components
    const [isHub, setIsHub] = useState(false);
    const [hubActiveThematique, setHubActiveThematique] = useState(null);
    const location = useLocation();
    const { user, dashboardPrefs } = useAuthStore();
    const { startReview, startSmartQuiz, startExamenMelange, startQuiz, quizInfo: storeQuizInfo } = useQuizStore();
    const { prefs, setTabForScope, setCategoryForScope, setSousSujetForScope, setLastScope, setLastActiveThematique, setParcoursForScope } = useDashboardPrefs(dashboardPrefs);
    const { setScopeNav, clearScopeNav } = useNavStore();
    const { sharedThematiques, fetchThematiques: fetchCustomThematiques } = useCustomStore();

    // Detect if we're under /compte/quiz/:scope or /:scope (public)
    const isCompte = location.pathname.startsWith('/compte/');
    const basePath = isCompte ? `/compte/quiz/${scope}` : `/${scope}`;
    const selectedScopes = prefs.selectedScopes || [];

    // Route privée /compte/quiz/:scope → exige login (préserve query+hash pour
    // le retour, ex. lien email "Reprendre" qui contient ?thematique=...#reprise-rapide).
    if (isCompte && !user) return <Navigate to={loginUrl()} replace />;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const VALID_TAB_IDS = [...TABS.map(t => t.id), ...(isMaThematique ? [TAB_GENERATIONS.id] : [])];
    const [activeTab, setActiveTab] = useState(() => {
        // Ouverture directe sur un onglet : ?onglet=redaction. Sert aux liens qui
        // promettent quelque chose de précis, un mail d'annonce par exemple, pour que
        // le lecteur arrive sur ce qu'on lui a annoncé et non sur l'onglet par défaut.
        // Ce choix l'emporte sur le dernier onglet mémorisé, qui n'a pas été demandé ici.
        const demande = new URLSearchParams(window.location.search).get('onglet');
        if (demande && VALID_TAB_IDS.includes(demande)) return demande;
        const saved = isCompte && prefs.lastTab?.[scope];
        if (saved && VALID_TAB_IDS.includes(saved)) return saved;
        return 'quiz';
    });
    const [filterFreeOnly, setFilterFreeOnly] = useState(false);
    const [guidedOpen, setGuidedOpen] = useState(false);
    const [guidedAuto, setGuidedAuto] = useState(false); // ouvert par « Commencer » : lance la séance conseillée
    useEffect(() => { setGuidedOpen(false); }, [activeTab]);
    // Lien direct ?bilan=1 (mails) : ouvre le parcours guidé et lance un bilan, une seule
    // fois, après le chargement de la thématique (qui peut remettre l'onglet mémorisé).
    // Déclaré APRÈS la fermeture ci-dessus : sur un changement d'onglet, l'ouverture gagne.
    const bilanDemande = useRef(new URLSearchParams(window.location.search).get('bilan') === '1');
    useEffect(() => {
        if (!bilanDemande.current || loading || !user) return;
        if (activeTab === 'resultats') { setActiveTab('quiz'); return; }
        bilanDemande.current = false;
        const url = new URL(window.location.href);
        url.searchParams.delete('bilan');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
        setGuidedAuto('bilan');
        setGuidedOpen(true);
    }, [loading, activeTab, user]);

    // All scopes for quick switcher
    const [allScopes, setAllScopes] = useState([]);

    // Collapsible categories
    const [openCategories, setOpenCategories] = useState({});

    // Historique
    const [histLoading, setHistLoading] = useState(false);
    const [histEntries, setHistEntries] = useState([]);
    const [histLoaded, setHistLoaded] = useState(false);

    // Stats
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsData, setStatsData] = useState(null);
    const [statsLoaded, setStatsLoaded] = useState(false);

    // Entrainement (mastery)
    const [masteryLoading, setMasteryLoading] = useState(false);
    const [masteryData, setMasteryData] = useState(null);
    const [masteryLoaded, setMasteryLoaded] = useState(false);
    const [smartQuizLoading, setSmartQuizLoading] = useState(false);
    const [smartQuizActive, setSmartQuizActive] = useState(false);

    // Erreurs
    const [errorMode, setErrorMode] = useState('never');
    const [errorCatId, setErrorCatId] = useState(0);
    const [errorLoading, setErrorLoading] = useState(false);
    const [errorSearchDone, setErrorSearchDone] = useState(false);
    const [errorSujets, setErrorSujets] = useState([]);
    const [errorPlayable, setErrorPlayable] = useState(0); // erreurs encore rejouables (droits, concours, en ligne)
    const [errorTotal, setErrorTotal] = useState(0);
    const [errorPages, setErrorPages] = useState(0);
    const [errorPage, setErrorPage] = useState(1);
    // Error quiz active (fullscreen overlay)
    const [errorQuizActive, setErrorQuizActive] = useState(false);
    const [errorQuizLoading, setErrorQuizLoading] = useState(false);

    // Resultats sub-tab
    const [resultatsSub, setResultatsSubRaw] = useState(() => {
        try { return localStorage.getItem('monquiz_resultats_subtab') || 'historique'; } catch { return 'historique'; }
    });
    const setResultatsSub = (tab) => { setResultatsSubRaw(tab); try { localStorage.setItem('monquiz_resultats_subtab', tab); } catch {} };

    // Examen blanc
    const [examenNbQuestions, setExamenNbQuestions] = useState(20);
    const [examenCategories, setExamenCategories] = useState([]);
    const [examenDiffMin, setExamenDiffMin] = useState(1);
    const [examenDiffMax, setExamenDiffMax] = useState(3);
    const [examenLoading, setExamenLoading] = useState(false);
    const [examenActive, setExamenActive] = useState(false);

    // Sous-sujets ignorés par l'utilisateur
    const [ignoredIds, setIgnoredIds] = useState(new Set());

    // Subscription wall popup
    const [showSubWall, setShowSubWall] = useState(false);
    // Mobile : description et compteurs secondaires de l'en-tête repliés derrière « Infos » (audit mobile 18/10).
    const [headerInfoOpen, setHeaderInfoOpen] = useState(false);
    // Subscription modal (direct payment)
    const [subModalInfo, setSubModalInfo] = useState(null);
    // Pricing preview for non-logged users
    const [showPricingPreview, setShowPricingPreview] = useState(false);

    // Fullscreen quiz modal
    const [modalSousSujetId, setModalSousSujetId] = useState(null);
    const [modalMode, setModalMode] = useState(null); // 'quiz' | 'apprentissage'
    const [modalNbQuestions, setModalNbQuestions] = useState(null);
    const [modalQuestionIds, setModalQuestionIds] = useState(null); // for refaire
    const [modalOrigin, setModalOrigin] = useState('subject'); // 'subject' or 'history:{id}'
    // Unified launch picker (nb questions + mode — remplace les 2 anciens modals)
    const [launchPicker, setLaunchPicker] = useState(null); // { sousSujetId, nbMax, nbSelected, mode }

    // Scroll to last sous-sujet
    const lastSsRef = useRef(null);
    const scrolledRef = useRef(false);

    // Quiz overlay background from matiere image
    const quizOverlayBg = storeQuizInfo?.matiere_image ? {
        backgroundImage: `url(${storeQuizInfo.matiere_image})`,
    } : {};

    // Tabs scrollable nav removed — replaced by the decision mode dock.

    // Scope switcher scrollable nav
    // Sort scopes according to user-defined order
    const sortScopes = useCallback((scopes) => {
        const order = prefs.scopeOrder || [];
        if (order.length === 0) return scopes;
        return [...scopes].sort((a, b) => {
            const ia = order.indexOf(a.slug);
            const ib = order.indexOf(b.slug);
            if (ia === -1 && ib === -1) return 0;
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });
    }, [prefs.scopeOrder]);

    // Load all scopes for quick switcher
    useEffect(() => {
        api.get('/scopes/list.php').then(({ data: res }) => setAllScopes(res.data || [])).catch(e => console.warn('Erreur chargement:', e));
        if (user) fetchCustomThematiques();
    }, []);

    // Sync scope nav to header store (desktop uses it, mobile uses sl-scope-nav)
    useEffect(() => {
        const hubScope = allScopes.find(s => s.slug === 'mes-thematiques');
        const standardScopes = allScopes.filter(s =>
            s.slug !== 'mes-thematiques' && s.slug !== 'thematique-personnalisable'
            && (s.content_source || 'editorial') !== 'user'
        );
        const filtered = selectedScopes.length > 0
            ? sortScopes(standardScopes.filter(s => selectedScopes.includes(s.slug)))
            : (isCompte ? [] : sortScopes(standardScopes));

        // Add active shared thématiques (not already in filtered, not hidden)
        const filteredSlugs = new Set(filtered.map(s => s.slug));
        const hiddenSharedNav = prefs.hiddenSharedScopes || [];
        const hubImage = hubScope?.image || null;
        const sharedScopes = (sharedThematiques || [])
            .filter(st => st.has_access && !filteredSlugs.has(st.slug) && !hiddenSharedNav.includes(st.slug))
            .map(st => ({
                ...st,
                scope_id: st.scope_id,
                is_shared: true,
                label: st.label.replace(/^Ma Thematique — /, ''),
                image: st.image || hubImage,
            }));

        // Trier l'ensemble (éditoriales + partagées) par scopeOrder → les partagées
        // s'intercalent à leur position choisie au lieu de rester collées à la fin.
        const ordered = sortScopes([...filtered, ...sharedScopes]);
        const visibleScopes = hubScope ? [hubScope, ...ordered] : ordered;
        const hasMore = standardScopes.length > filtered.length;
        if (visibleScopes.length > 0) {
            setScopeNav({ scopes: visibleScopes, currentScope: scope, isCompte, hasMore: hasMore || isCompte, noneSelected: filtered.length === 0 });
        } else {
            clearScopeNav();
        }
        // Pas de clear au démontage : le menu de thématiques PERSISTE sur les autres
        // pages de l'espace connecté (Activer un code, réglages…). Le Header décide où
        // l'afficher et le masque uniquement sur la config des thématiques / l'admin / le quiz.
    }, [allScopes, scope, selectedScopes, isCompte, setScopeNav, clearScopeNav, sortScopes, sharedThematiques]);

    // Scope nav scroll removed — now handled by header scope nav

    // Toggle : show/hide OBSOLETE user sujets in "Non classé". We always fetch
    // them from the backend (so no refetch on toggle, no scroll jump) and just
    // filter client-side in TabQuiz.
    const [showObsolete, setShowObsolete] = useState(false);

    // Notions à la carte de cette thématique (vide tant qu'elles ne sont pas visibles pour la personne)
    const [notionsData, setNotionsData] = useState({ loaded: false, notions: [], preview: false });
    const [notionsFocus, setNotionsFocus] = useState(false); // ouverture par l'encart « notions à choisir » : amener l'en-tête à l'écran
    const notionsScopeId = data?.scope?.id;
    const notionsEligible = !!user && !!notionsScopeId && data?.scope?.content_source !== 'user' && !data?.scope?.is_hub;
    const loadNotions = useCallback(() => {
        if (!notionsScopeId) { setNotionsData({ loaded: false, notions: [], preview: false }); return; } // thématique pas encore chargée
        if (!notionsEligible) { setNotionsData({ loaded: true, notions: [], preview: false }); return; }
        api.get('/notions/list.php', { params: { scope_id: notionsScopeId } })
            .then(({ data: res }) => setNotionsData({ loaded: true, notions: res.data?.notions || [], preview: !!res.data?.preview_mode, offerCredits: res.data?.offer_credits || 0, packCredits: res.data?.pack_credits || 0, packs: res.data?.packs || [] }))
            .catch(() => setNotionsData({ loaded: true, notions: [], preview: false }));
    }, [notionsEligible, notionsScopeId]);
    useEffect(() => { loadNotions(); }, [loadNotions]);
    useEffect(() => {
        // Onglet « Notions » mémorisé alors qu'aucune notion n'est proposée : retour aux sujets.
        if (activeTab === 'notions' && notionsData.loaded && notionsData.notions.length === 0) setActiveTab('quiz');
    }, [activeTab, notionsData]);

    // Fetch scope data — extracted so it can be called after generation completes
    const fetchData = useCallback(() => {
        return api.get('/scopes/detail.php', { params: { slug: scope, include_obsolete: 1 } })
            .then(({ data: res }) => {
                setData(res.data);
                const cs = res.data?.scope?.content_source;
                setIsUserThematique(cs === 'user' && !res.data?.scope?.is_hub);
                setIsPartnerScope(cs === 'partner');
                setIsHub(!!res.data?.scope?.is_hub);
                if (isCompte) {
                    localStorage.setItem(LAST_SCOPE_KEY, scope);
                    setLastScope(scope);
                }
                const cats = res.data?.categories || [];
                const savedCatId = prefs.lastCategories?.[scope];
                const savedSsId = prefs.lastSousSujets?.[scope];
                const ssCat = savedSsId && cats.find(c => c.sous_sujets?.some(ss => ss.id === savedSsId));
                const catToOpen = (savedCatId && cats.some(c => c.id === savedCatId))
                    ? savedCatId
                    : ssCat ? ssCat.id
                    : cats[0]?.id;
                if (catToOpen) {
                    setOpenCategories(prev => {
                        const validOpen = Object.keys(prev).filter(id => prev[id] && cats.some(c => String(c.id) === String(id)));
                        if (validOpen.length > 0) return prev;
                        return { [catToOpen]: true };
                    });
                }
            })
            .catch(() => setData(null));
    }, [scope, isCompte]);

    // Load scope data
    useEffect(() => {
        setLoading(true);
        setHistLoaded(false);
        setStatsLoaded(false);
        setMasteryLoaded(false);
        setErrorSearchDone(false);
        // Même règle qu'au montage : un onglet demandé dans l'URL l'emporte sur le
        // dernier mémorisé. Sans cela, cet effet écrasait aussitôt le choix du lien.
        const demande = new URLSearchParams(window.location.search).get('onglet');
        const savedTab = isCompte && prefs.lastTab?.[scope];
        if (demande && VALID_TAB_IDS.includes(demande)) {
            setActiveTab(demande);
        } else if (savedTab && VALID_TAB_IDS.includes(savedTab)) {
            setActiveTab(savedTab);
        } else {
            setActiveTab('quiz');
        }
        fetchData().finally(() => setLoading(false));
    }, [scope, fetchData]);

    // Load ignored topics (once, when user is logged in)
    useEffect(() => {
        if (!user) return;
        api.get('/user/ignored-topics.php')
            .then(({ data: res }) => setIgnoredIds(new Set(res.data?.ignored || [])))
            .catch(e => console.warn('Erreur chargement:', e));
    }, [user]);

    const handleToggleIgnore = useCallback((ssId) => {
        api.post('/user/ignored-topics.php', { sous_sujet_id: ssId })
            .then(({ data: res }) => {
                setIgnoredIds(prev => {
                    const next = new Set(prev);
                    if (res.data?.action === 'ignored') next.add(ssId);
                    else next.delete(ssId);
                    return next;
                });
            })
            .catch(e => console.warn('Erreur chargement:', e));
    }, []);

    // Load tab data on tab change
    useEffect(() => {
        if (!data || !user) return;
        const scopeId = data.scope.id;

        if (activeTab === 'resultats') {
            if (!histLoaded) {
                setHistLoading(true);
                api.get('/qcm/history.php', { params: { scope_id: scopeId } })
                    .then(({ data: res }) => { setHistEntries(res.data || []); setHistLoaded(true); })
                    .catch(e => console.warn('Erreur chargement:', e))
                    .finally(() => setHistLoading(false));
            }
            if (!statsLoaded) {
                setStatsLoading(true);
                api.get('/user/stats.php', { params: { scope_id: scopeId } })
                    .then(({ data: res }) => { setStatsData(res.data); setStatsLoaded(true); })
                    .catch(e => console.warn('Erreur chargement:', e))
                    .finally(() => setStatsLoading(false));
            }
        }

        if (activeTab === 'entrainement' && !masteryLoaded) {
            setMasteryLoading(true);
            api.get('/user/mastery.php', { params: { scope_id: scopeId } })
                .then(({ data: res }) => { setMasteryData(res.data); setMasteryLoaded(true); })
                .catch(e => console.warn('Erreur chargement:', e))
                .finally(() => setMasteryLoading(false));
        }
        // masteryLoaded/histLoaded/statsLoaded dans les deps : quand onQuizDone les remet à
        // false (fin de quiz OU retour d'apprentissage guidé), l'effet se relance et refetch
        // les données de l'onglet courant sans changer d'onglet.
        // PORTÉE ASSUMÉE DU SÉLECTEUR DE PARCOURS : historique, statistiques et
        // maîtrise restent à l'échelle de la thématique, volontairement. Ce qui est
        // DEMANDÉ au candidat respecte son parcours — catalogue, mélange, quiz
        // intelligent, révisions, erreurs et QRC sont filtrés jusqu'à la requête
        // serveur. Ce qu'il a DÉJÀ FAIT lui est montré en entier : masquer ses
        // propres sessions passées parce qu'il a changé de bouton serait pire que
        // de les afficher toutes. Ces trois appels ne portent donc pas de parcours
        // et ne sont pas rechargés quand il change.
    }, [activeTab, data, user, masteryLoaded, histLoaded, statsLoaded]);

    // Mesure d'usage (compteurs admin, /api/user/feature-ping.php) : arrivée sur la thématique,
    // puis chaque onglet ouvert, et la vue choisie dans Résultats. Ignoré pour l'admin en simulation.
    const scopeIdForPing = data?.scope?.id;
    useEffect(() => {
        if (!user || !scopeIdForPing || isHub) return;
        pingFeature('scope_landing');
    }, [user, scopeIdForPing, isHub]);
    useEffect(() => {
        if (!user || !scopeIdForPing || isHub) return;
        pingFeature(`tab_${activeTab}`);
        if (activeTab === 'resultats') pingFeature(resultatsSub === 'stats' ? 'stats' : 'history');
    }, [user, scopeIdForPing, isHub, activeTab, resultatsSub]);

    // Scroll to last sous-sujet after data loads
    useEffect(() => {
        if (!data || !isCompte || scrolledRef.current) return;
        const savedSsId = prefs.lastSousSujets?.[scope];
        if (!savedSsId) return;
        scrolledRef.current = true;
        // Wait for DOM to render the category content
        requestAnimationFrame(() => {
            const el = document.querySelector(`[data-ss-id="${savedSsId}"]`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
    }, [data]);

    // Reset scroll flag when scope changes
    useEffect(() => {
        scrolledRef.current = false;
    }, [scope]);

    const toggleCategory = (catId) => {
        setOpenCategories((prev) => {
            const isOpen = !!prev[catId];
            if (!isOpen && isCompte) setCategoryForScope(scope, catId);
            // Scroll vers la catégorie après ouverture
            if (!isOpen) {
                setTimeout(() => {
                    document.getElementById(`cat-${catId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
            // Accordion exclusif : ferme toutes les autres
            return isOpen ? {} : { [catId]: true };
        });
    };

    usePageTitle(data?.scope?.label ? `${data.scope.label} — QCM` : null);

    // Changement de concours : les erreurs affichées et la catégorie choisie relèvent de
    // l'ancien périmètre. On les efface, comme au changement de mode ou de catégorie
    // (la recherche repart du bouton). Choix brut des préférences : parcoursActif n'est
    // connu qu'après les retours anticipés ci-dessous.
    const parcoursChoisi = prefs.parcoursByScope?.[scope] || '';
    useEffect(() => { setErrorSearchDone(false); setErrorCatId(0); }, [parcoursChoisi]);
    // Questions du quiz qui vient de se terminer : l'écran de fin ne doit pas proposer
    // comme « sujet suivant » un sujet qu'on vient de jouer (l'arbre n'est rechargé
    // qu'à la fermeture du quiz).
    const finishedQuestions = useQuizStore(s => (s.finished ? s.questions : null));

    if (loading) return <div className="loading">Chargement...</div>;
    if (!data) return <div className="not-found">Thematique introuvable.</div>;

    const { scope: scopeInfo, has_access, access_end, access_type, is_gift, is_shared, formateur_tracked, had_access, shared_by, parcours: parcoursList = [], categories: toutesCategories } = data;
    // Parcours : une thématique peut abriter plusieurs concours. Le choix est rangé
    // dans les préférences du compte, donc il suit d'un appareil à l'autre ; vide =
    // « Tout afficher », le défaut. Tout ce qui suit consomme l'arbre FILTRÉ, si bien
    // que les onglets et les compteurs du dock s'accordent sans y toucher un à un.
    const parcoursMemorise = normaliserParcours(parcoursList, prefs.parcoursByScope?.[scope] || '');
    // Sur « Mes résultats », le filtre est NEUTRALISÉ pour l'affichage : historique,
    // statistiques et maîtrise portent sur toute la thématique, et les compteurs de
    // l'encart doivent dire la même chose que ce qu'on lit en dessous. Le choix n'est
    // pas effacé, seulement mis en sommeil : il revient dès qu'on quitte l'onglet.
    const parcoursActif = activeTab === 'resultats' ? '' : parcoursMemorise;
    const categories = filtrerParParcours(toutesCategories, parcoursList, parcoursActif);
    // Notions du parcours choisi : sans parcours = communes à tous (même règle que les sujets, migration 185).
    const notionsVisibles = parcoursActif ? notionsData.notions.filter(n => !(n.parcours || []).length || n.parcours.includes(parcoursActif)) : notionsData.notions;
    // Notions encore à choisir, seulement s'il en reste une payante à prendre dans ce parcours
    const notionsCredits = !notionsData.preview && notionsVisibles.some(isBuyable)
        ? (notionsData.offerCredits || 0) + (notionsData.packCredits || 0) : 0;
    setParcoursActif(scopeInfo?.id, scope, parcoursActif);
    // Lecture seule : thématique partagée OU perso dont on n'est ni propriétaire ni
    // admin (ex. accès partagé expiré) → empêche toute UI propriétaire (gestion,
    // suivi des résultats…) de fuiter vers un non-propriétaire.
    const isReadOnly = is_shared || (isMaThematique && access_type !== 'owner' && access_type !== 'admin');
    // Accès partagé (actif OU expiré) à une perso qu'on ne possède pas → on propose
    // « Demander une prolongation » au propriétaire.
    const isSharedPersoAccess = isMaThematique && access_type !== 'owner' && access_type !== 'admin' && (is_shared || had_access);
    // Compte gratuit sur une thématique achetable : « Débloquer l'accès » en en-tête et en bouton flottant.
    const proposeAcces = !!user && !has_access && !is_shared && !isReadOnly;
    // Prix d'entrée réel (le plus petit montant à payer), lu en base par thématique.
    const prixEntree = (() => {
        const prix = [scopeInfo?.price_1m, scopeInfo?.price_6m, scopeInfo?.price_12m].map(p => parseFloat(p)).filter(p => p > 0);
        if (!prix.length) return null;
        const min = Math.min(...prix);
        return `${Number.isInteger(min) ? min : min.toFixed(2).replace('.', ',')} €`;
    })();
    const ouvrirAbonnement = (source) => setSubModalInfo({ scopeId: scopeInfo.id, label: scopeInfo.label, extraBody: scopeInfo.thematique_id ? { user_thematique_id: scopeInfo.thematique_id } : {}, source });

    // Tentative d'accès (par code/URL) à une thématique perso sans aucun droit :
    // ni propriétaire, ni admin, ni accès actif (partagé/abonné) → page propre
    // « pas d'accès » (aucun onglet, aucune gestion, aucun bouton S'abonner).
    if (isMaThematique && !has_access && access_type !== 'owner' && access_type !== 'admin' && !had_access) {
        const cleanLabel = (scopeInfo.label || 'cette thématique').replace(/^Ma Thematique — /, '');
        return (
            <div className="scope-landing-v2">
                <div className="sl-login-prompt" style={{ textAlign: 'center', maxWidth: 560, margin: '48px auto' }}>
                    <Lock size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
                    <h1 style={{ fontSize: '1.4rem', marginBottom: 12 }}>{cleanLabel}</h1>
                    <p style={{ marginBottom: 8 }}>
                        {had_access
                            ? <>Votre accès à la thématique « <strong>{cleanLabel}</strong> » a expiré.</>
                            : <>Vous n'avez pas accès à la thématique « <strong>{cleanLabel}</strong> ».</>}
                    </p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                        {had_access
                            ? <>Pour le renouveler ou en cas de problème, contactez-nous : <a href="mailto:contact@monquiz.fr">contact@monquiz.fr</a></>
                            : <>En cas de problème, contactez-nous : <a href="mailto:contact@monquiz.fr">contact@monquiz.fr</a></>}
                    </p>
                </div>
            </div>
        );
    }

    // === ERROR HANDLERS ===
    const searchErrors = async (p = 1) => {
        if (p === 1) pingFeature('errors_list');
        setErrorLoading(true);
        setErrorSearchDone(false);
        try {
            const { data: res } = await api.post('/qcm/errors.php', {
                scope_id: scopeInfo.id,
                mode: errorMode,
                categorie_id: errorCatId || undefined,
                page: p,
            });
            const r = res.data;
            setErrorSujets(r.by_sujet || []); setErrorPlayable(r.playable_total ?? r.total_questions);
            setErrorTotal(r.total_questions);
            setErrorPages(r.total_pages);
            setErrorPage(r.page);
            setErrorSearchDone(true);
        } catch (e) {
            console.warn('Erreur recherche erreurs:', e);
            setErrorSujets([]); setErrorPlayable(0);
            setErrorTotal(0);
            setErrorSearchDone(true);
        }
        setErrorLoading(false);
    };

    // Removed — error quiz now handled by quizStore.startErrorsQuiz + errorQuizActive overlay

    // Démarrage direct (bypass picker) — depuis popup détail ou reprise session
    const directLaunch = (sousSujetId, mode, nb) => {
        if (isCompte) setSousSujetForScope(scope, sousSujetId);
        setModalSousSujetId(sousSujetId);
        setModalMode(mode);
        setModalNbQuestions(nb);
        setModalQuestionIds(null);
        setModalOrigin('subject');
    };

    // Handle clicking "Commencer" sur une card — ouvre le picker unifié (nb + mode)
    const handleStartSousSujet = (sousSujetId, sessionMode, nbQuestions) => {
        if (isCompte) setSousSujetForScope(scope, sousSujetId);
        if (sessionMode) {
            directLaunch(sousSujetId, sessionMode, null);
        } else {
            setLaunchPicker({
                sousSujetId,
                nbMax: nbQuestions || 0,
                nbSelected: Math.min(10, nbQuestions || 10),
                mode: 'apprentissage',
                // Secondes par question constatées (durée estimée, migration 193)
                secPerQ: categories.flatMap(c => c.sous_sujets).find(ss => ss.id === sousSujetId)?.sec_per_q || null,
            });
        }
    };

    // Guard : verifie l'abonnement avant une action premium
    const requireAccess = (action) => {
        if (!has_access) {
            setShowSubWall(activeTab === 'examen' ? 'melange' : activeTab);
            return false;
        }
        if (action) action();
        return true;
    };

    // Accès au MOTEUR (progression, révisions, erreurs, apprentissage guidé).
    // Volontairement plus permissif que `has_access` : dès qu'une personne a
    // répondu à des questions — donc via les sujets d'essai gratuits — elle peut
    // exploiter le moteur SUR CE QU'ELLE A DÉJÀ FAIT. Le serveur applique la même
    // règle depuis toujours (smart-quiz, sm2-due, learning-plan) et restreint le
    // pool aux questions déjà vues ; seule l'interface le cachait.
    // Le mur d'abonnement reste entier sur ce qui compte : le volume de contenu.
    const hasEngineHistory = (masteryData?.summary?.total_questions || 0) > 0;
    const hasDemo = categories.some(c => c.sous_sujets.some(ss => ss.is_demo && ss.nb_questions > 0));
    const engineAccess = has_access || hasEngineHistory || (!!user && hasDemo);

    // Meme philosophie pour l'onglet Resultats, mais adossee a l'historique que
    // CET onglet charge lui-meme : `masteryData` n'est fetche que par l'onglet
    // Entrainement et vaudrait null ici, ce qui verrouillerait a tort.
    const resultsAccess = has_access || histEntries.length > 0;

    // Fermeture du quiz plein ecran : partagee entre la croix du quiz et les
    // actions de l'ecran de fin, qui doivent ramener au meme etat propre.
    const closeQuizModal = () => {
        useQuizStore.getState().reset();
        setModalSousSujetId(null);
        setModalMode(null);
        setModalNbQuestions(null);
        setModalQuestionIds(null);
        setModalOrigin('subject');
        setSmartQuizActive(false);
        setErrorQuizActive(false);
        setExamenActive(false);
        setHistLoaded(false);
        setStatsLoaded(false);
        setMasteryLoaded(false);
        api.get('/scopes/detail.php', { params: { slug: scope } })
            .then(({ data: res }) => setData(res.data))
            .catch(e => console.warn('Erreur chargement:', e));
    };

    // Ecran de fin de quiz — ce qu'on propose apres une bonne seance.
    // Sans ces deux props, la seule action offerte etait « Revoir mes reponses » :
    // une impasse juste apres un sans-faute, precisement au moment ou la personne
    // est la plus disponible pour aller plus loin.
    // Le suivant est lancé directement (même mode, 10 questions) : revenir à la liste
    // obligeait à choisir soi-même, et Marie-Laure (user 42) a refait 7 fois le même sujet.
    const nextSubject = !isReadOnly ? pickNextSubject(categories, {
        hasAccess: has_access,
        currentId: modalSousSujetId,
        excludeIds: (finishedQuestions || []).map(q => q.sous_sujet_id),
    }) : null;
    const backToList = () => { closeQuizModal(); setActiveTab('quiz'); if (isCompte) setTabForScope(scope, 'quiz'); };
    const shortLabel = (l) => (l.length > 48 ? `${l.slice(0, 46).trimEnd()}…` : l);
    const verdictAction = nextSubject ? {
        label: `${has_access ? 'Sujet suivant' : 'Sujet d’essai suivant'} : ${shortLabel(nextSubject.label)}`,
        onClick: () => {
            const mode = useQuizStore.getState().mode || 'apprentissage';
            backToList();
            // Après la fermeture (rendu suivant) : le quiz se remonte proprement sur le nouveau sujet.
            setTimeout(() => directLaunch(nextSubject.id, mode, Math.min(10, nextSubject.nb_questions)), 0);
        },
    } : {
        label: 'Continuer sur un autre sujet',
        onClick: backToList,
    };

    // Proposition d'abonnement : uniquement pour un compte sans acces, et
    // uniquement chiffree (volume reel encore verrouille + prix d'entree reel).
    const verdictUpsell = (() => {
        if (has_access || isReadOnly || is_shared || isMaThematique || !scopeInfo) return null;
        const prices = [scopeInfo.price_1m, scopeInfo.price_6m, scopeInfo.price_12m]
            .map(p => parseFloat(p)).filter(p => p > 0);
        if (!prices.length) return null;
        const lockedQuestions = (categories || [])
            .flatMap(c => c.sous_sujets || [])
            .filter(ss => !ss.is_demo)
            .reduce((n, ss) => n + (ss.nb_questions || 0), 0);
        if (!lockedQuestions) return null;
        const cov = programmeCoverage(categories);
        const seenPart = cov.seen > 0 ? `Tu as vu ${cov.seen.toLocaleString('fr-FR')} questions sur ${cov.total.toLocaleString('fr-FR')} (${cov.pctLabel} des questions de la thématique). ` : '';
        return {
            title: `Débloquer toute la préparation ${scopeInfo.label}`,
            subtitle: `${seenPart}${lockedQuestions.toLocaleString('fr-FR')} questions supplémentaires, les mélanges de sujets et le suivi complet — à partir de ${Math.min(...prices)} €, paiement unique.`,
            cta: 'Voir les tarifs',
            source: 'fin_quiz', scopeId: scopeInfo.id, // mesure upsell_shown (MasteryVerdict)
            onClick: () => { closeQuizModal(); setSubModalInfo({ scopeId: scopeInfo.id, label: scopeInfo.label, extraBody: {}, source: 'fin_quiz' }); },
        };
    })();
    const requireEngine = (action) => {
        if (!engineAccess) {
            setShowSubWall('entrainement');
            return false;
        }
        if (action) action();
        return true;
    };

    // Carte « Bon retour » : ses raccourcis lancent les mêmes séances que l'onglet Entraînement
    // (où le quiz s'affiche), puis basculent sur cet onglet.
    const wbOpts = () => ({
        isCustom: scopeInfo?.content_source === 'user',
        isPartner: scopeInfo?.content_source === 'partner',
        playMode: 'quiz',
        scopeImage: scopeInfo?.image || null,
    });
    const allerEntrainement = () => { setActiveTab('entrainement'); if (isCompte) setTabForScope(scope, 'entrainement'); };
    const launchWbSm2 = async (nb) => {
        setSmartQuizLoading(true);
        // Concours choisi transmis : sans lui, les révisions dues sortaient du parcours (Inspecteur / Contrôleur).
        const ok = await useQuizStore.getState().startSm2DueQuiz(scopeInfo.id, nb, { ...wbOpts(), parcours: parcoursActif || undefined });
        setSmartQuizLoading(false);
        if (ok) { setSmartQuizActive(true); allerEntrainement(); }
    };
    // Séance express du dock « Ta prochaine étape » : mêmes révisions que le parcours guidé
    // (mode apprentissage, concours choisi), en plus court.
    const launchSm2Express = async (nb) => {
        setSmartQuizLoading(true);
        const ok = await useQuizStore.getState().startSm2DueQuiz(scopeInfo.id, nb, { ...wbOpts(), playMode: 'apprentissage', parcours: parcoursActif || undefined });
        setSmartQuizLoading(false);
        if (ok) { setSmartQuizActive(true); allerEntrainement(); }
    };
    const launchWbErrors = async (sousSujetId) => {
        setErrorQuizLoading(true);
        const ok = await useQuizStore.getState().startErrorsQuiz(scopeInfo.id, 'never', 20, { ...wbOpts(), sousSujetId });
        setErrorQuizLoading(false);
        if (ok) { setErrorQuizActive(true); allerEntrainement(); }
    };

    return (
        <div className="scope-landing-v2">
            {/* Top bar — only for public view (not /compte) */}
            {!isCompte && (
                <div className="sl-topbar">
                    <Link to="/#thematiques" className="sl-back">
                        ← Accueil
                    </Link>
                </div>
            )}

            {/* Scope switcher removed — now always in header scope nav */}

            {/* Hub mode: hub renders nav → header → content all in one flow */}
            {isHub && <MesThematiquesHub hubImage={scopeInfo?.image} hubColor={scopeInfo?.couleur_theme} onActiveChange={setHubActiveThematique}
                initialActiveSlug={prefs.lastActiveThematique}
                onActiveSlugChange={setLastActiveThematique}
                headerSlot={(hat, actions, modeSwitch) => {
                    const headerImage = hat ? (hat.image || scopeInfo.image) : scopeInfo.image;
                    const headerColor = hat ? (hat.couleur_theme || scopeInfo.couleur_theme) : scopeInfo.couleur_theme;
                    const headerLabel = hat ? hat.label : scopeInfo.label;
                    const headerDesc = hat ? (hat.description || scopeInfo.description) : scopeInfo.description;
                    return (
                        <div className={`sl-header ${headerImage ? 'sl-header--has-image' : ''}`}
                            style={headerColor ? { '--scope-accent': headerColor } : undefined}>
                            {headerImage && <div className="sl-header-hero"><img src={headerImage} alt="" className="sl-header-hero-img" /><div className="sl-header-hero-overlay" /></div>}
                            <div className="sl-header-content">
                                <div className="sl-header-info">
                                    <div className="sl-header-title-row">
                                        {scopeInfo.icon && !headerImage && <ScopeIllustration icon={scopeInfo.icon} className="content-scope-illu" />}
                                        <h1 className="sl-title">{headerLabel}</h1>
                                    </div>
                                    {actions?.personnaliser && (
                                        <div className="sl-header-personnaliser">{actions.personnaliser}</div>
                                    )}
                                    {headerDesc && <p className="sl-description">{headerDesc}</p>}
                                    {user && hat && (
                                        <div className="sl-header-stats">
                                            <span className="sl-mini-stat sl-mini-stat--secondary"><ClipboardList size={14} /> <span className="sl-mini-stat-value">{hat.nb_categories || 0}</span> catégories</span>
                                            <span className="sl-mini-stat sl-mini-stat--secondary"><BookOpen size={14} /> <span className="sl-mini-stat-value">{hat.nb_sous_sujets || 0}</span> sujets</span>
                                            <span className="sl-mini-stat"><CheckSquare size={14} /> <span className="sl-mini-stat-value">{hat.nb_questions || 0}</span> questions</span>
                                        </div>
                                    )}
                                </div>
                                {hat && (
                                    <div className="sl-header-actions sl-header-actions--stacked">
                                        {!!Number(hat.has_access) && hat.subscription_end && (
                                            <span className="sl-badge-active">
                                                Accès jusqu'au <strong>{new Date(hat.subscription_end + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                                            </span>
                                        )}
                                        {!!Number(hat.has_access) && !hat.subscription_end && (
                                            <span className="sl-badge-active">Accès complet</span>
                                        )}
                                        {!hat.has_access && (
                                            <button
                                                className="sl-badge-expired"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setSubModalInfo({ scopeId: scopeInfo.id, label: hat.label, extraBody: { user_thematique_id: hat.id }, source: 'renouveler_perso' })}
                                            >
                                                Renouveler
                                            </button>
                                        )}
                                        {actions?.gestion}
                                        {actions?.partager}
                                        {actions?.publier}
                                    </div>
                                )}
                                {modeSwitch}
                            </div>
                        </div>
                    );
                }}
            />}

            {/* Standard header — non-hub scopes only */}
            {!isHub && (() => {
                const headerImage = scopeInfo.image || (is_shared ? (allScopes.find(s => s.slug === 'mes-thematiques')?.image || null) : null);
                return (
            <div
                className={`sl-header ${headerImage ? 'sl-header--has-image' : ''}${headerInfoOpen ? ' is-info-open' : ''}`}
                style={scopeInfo.couleur_theme ? { '--scope-accent': scopeInfo.couleur_theme } : undefined}
            >
                {headerImage && (
                    <div className="sl-header-hero">
                        <img src={headerImage} alt="" className="sl-header-hero-img" />
                        <div className="sl-header-hero-overlay" />
                    </div>
                )}
                <div className="sl-header-content">
                    <div className="sl-header-info">
                        <div className="sl-header-title-row">
                            {scopeInfo.icon && !scopeInfo.image && (
                                <ScopeIllustration icon={scopeInfo.icon} className="content-scope-illu" />
                            )}
                            <h1 className="sl-title">{is_shared ? scopeInfo.label.replace(/^Ma Thematique — /, '') : scopeInfo.label}</h1>
                            <button type="button" className="sl-info-toggle" onClick={() => setHeaderInfoOpen(o => !o)} aria-expanded={headerInfoOpen}>
                                Infos {headerInfoOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                        </div>
                        {scopeInfo.description && <p className="sl-description">{scopeInfo.description}</p>}
                        {is_shared && shared_by && (
                            <p className="sl-shared-by">Partagé par {shared_by.prenom ? `${shared_by.prenom} (${shared_by.email})` : shared_by.email}</p>
                        )}
                        {user && (() => {
                            const totalQ = categories.reduce((s, c) => s + c.sous_sujets.reduce((s2, ss) => s2 + (ss.nb_questions || 0), 0), 0);
                            const totalSs = categories.reduce((s, c) => s + c.sous_sujets.length, 0);
                            const cov = programmeCoverage(categories);
                            const totalMastered = categories.reduce((s, c) => s + c.sous_sujets.reduce((s2, ss) => s2 + (ss.mastered_count || 0), 0), 0);
                            // Questions courtes : compté comme les autres, donc filtré par le
                            // parcours. Affiché seulement si la thématique en a, ce qui laisse
                            // l'encart des six autres inchangé. L'admin voit aussi les brouillons,
                            // puisque l'API les lui compte.
                            const totalQrc = categories.reduce((s, c) => s + c.sous_sujets.reduce((s2, ss) => s2 + (ss.qrc_count || 0), 0), 0);
                            return (
                                <div className="sl-header-stats">
                                    <span className="sl-mini-stat sl-mini-stat--secondary"><ClipboardList size={14} /> <span className="sl-mini-stat-value">{categories.length}</span> catégories</span>
                                    <span className="sl-mini-stat sl-mini-stat--secondary"><BookOpen size={14} /> <span className="sl-mini-stat-value">{totalSs}</span> sujets</span>
                                    <span className="sl-mini-stat"><CheckSquare size={14} /> <span className="sl-mini-stat-value">{totalQ}</span> questions</span>
                                    {totalQrc > 0 && (
                                        <span className="sl-mini-stat sl-mini-stat--secondary" title="Questions à réponse rédigée, corrigées par l’IA">
                                            <PenLine size={14} /> <span className="sl-mini-stat-value">{totalQrc}</span> {totalQrc > 1 ? 'questions rédigées' : 'question rédigée'}
                                        </span>
                                    )}
                                    {cov.seen > 0 && (
                                        <span className="sl-mini-stat sl-mini-stat--seen" title={`Questions différentes déjà vues sur le total ${parcoursActif ? 'de ton parcours' : 'de la thématique'} (ne mesure pas la couverture du programme officiel)`}>
                                            <Eye size={14} /> <span className="sl-mini-stat-value">{cov.seen.toLocaleString('fr-FR')}</span> / {cov.total.toLocaleString('fr-FR')} vues · {cov.pctLabel}
                                        </span>
                                    )}
                                    {totalMastered > 0 && (
                                        <span className="sl-mini-stat sl-mini-stat--ok" title="Questions réussies"><ThumbsUp size={14} /> <span className="sl-mini-stat-value">{totalMastered}</span></span>
                                    )}
                                    {!has_access && activeTab === 'quiz' && (
                                        <button
                                            className={`sl-filter-free-btn ${filterFreeOnly ? 'sl-filter-free-btn--active' : ''}`}
                                            onClick={() => setFilterFreeOnly(f => !f)}
                                            title={filterFreeOnly ? 'Afficher tout' : 'Afficher les sujets gratuits uniquement'}
                                        >
                                            <Filter size={14} />
                                            <span>Gratuits</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })()}
                        {(scopeInfo?.content_note || scopeInfo?.content_updated_at) && (
                            // Suivi de contenu (migration 180) : ligne d'info de la thématique, saisie dans l'admin
                            // Contenu ; la date seule s'affiche si aucune note n'est saisie.
                            <p className="sl-content-note">
                                <Sparkles size={14} aria-hidden="true" />
                                <span>
                                    {scopeInfo.content_updated_at && (
                                        <strong>Mis à jour le {new Date(scopeInfo.content_updated_at + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                                    )}
                                    {scopeInfo.content_updated_at && scopeInfo.content_note && ' · '}
                                    {scopeInfo.content_note}
                                </span>
                            </p>
                        )}
                        {(() => {
                            // Date d'écrit : à côté de « Je prépare » quand la thématique a des
                            // parcours (le parcours mémorisé choisit les dates proposées), seule sinon.
                            // Thématique communautaire (certifications ISTQB…) : « session d'examen ».
                            const examPill = user && !isReadOnly && scopeInfo?.id
                                ? <ExamDatePill scopeId={scopeInfo.id} parcours={parcoursMemorise}
                                    certif={scopeInfo.content_source === 'partner'} /> : null;
                            if (parcoursList.length >= 2 && activeTab !== 'resultats') return (
                                <ParcoursSwitch
                                    parcours={parcoursList}
                                    actif={parcoursActif}
                                    compteur={compterParParcours(toutesCategories, parcoursList)}
                                    onChange={(slug) => setParcoursForScope(scope, slug)}
                                >{examPill}</ParcoursSwitch>
                            );
                            return examPill ? <div className="parcours-switch">{examPill}</div> : null;
                        })()}
                    </div>
                    <div className="sl-header-actions">
                        {user && has_access && (() => {
                            const daysLeft = access_end ? Math.ceil((new Date(access_end + 'T23:59:59') - new Date()) / 86400000) : null;
                            // Pas de prolongation payante sur une thématique PARTAGÉE : l'accès
                            // est piloté par les codes du propriétaire (cf. bouton « Demander une
                            // prolongation » plus bas). admin = pas de durée.
                            const canExtend = access_type !== 'admin' && access_end && !is_shared;
                            const openExtend = () => setSubModalInfo({
                                scopeId: scopeInfo.id,
                                label: scopeInfo.label,
                                extraBody: scopeInfo.thematique_id ? { user_thematique_id: scopeInfo.thematique_id } : {},
                                source: 'prolonger',
                            });
                            return (
                                <>
                                    <span className="sl-badge-active">
                                        {access_type === 'admin' ? 'Admin ∞' :
                                         access_type === 'free_promo' ? <>🎁 Gratuit jusqu'au <strong>{new Date(access_end + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></> :
                                         access_end ? <>{is_gift && <Gift size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />}Accès jusqu'au <strong>{new Date(access_end + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></> :
                                         'Accès complet'}
                                    </span>
                                    {canExtend && daysLeft !== null && daysLeft <= 7 && (
                                        <div className="sl-expiry-warning">
                                            <span>{daysLeft <= 0 ? 'Votre accès expire aujourd\'hui !' : daysLeft === 1 ? 'Votre accès expire demain !' : `Votre accès expire dans ${daysLeft} jours`}</span>
                                            <button className="btn btn-xs btn-primary" onClick={openExtend}>Prolonger</button>
                                        </div>
                                    )}
                                    {canExtend && daysLeft !== null && daysLeft > 7 && (
                                        <button className="sl-badge-extend"
                                                onClick={openExtend}
                                                title="Acheter du temps supplémentaire qui s'ajoutera à la fin de votre accès actuel">
                                            + Prolonger
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                        {proposeAcces && (
                            <button className="btn btn-sm sl-cta-unlock" onClick={() => ouvrirAbonnement('entete')}>
                                {scopeInfo?.subscription_end ? 'Renouveler mon accès' : (
                                    <>Débloquer l’accès<span className="sl-cta-unlock__long"> complet</span>{prixEntree && <span className="sl-cta-unlock__prix"> · dès {prixEntree}</span>}</>
                                )}
                                <ArrowRight size={15} aria-hidden="true" />
                            </button>
                        )}
                        {/* Thématique partagée : pas d'abonnement payant. Demande de
                            prolongation au propriétaire — seulement quand l'accès approche
                            ou est expiré (< 7 j) ; au-delà ça n'a pas de sens. */}
                        {user && isSharedPersoAccess && scopeInfo.thematique_id && (() => {
                            const sDays = access_end ? Math.ceil((new Date(access_end + 'T23:59:59') - new Date()) / 86400000) : null;
                            if (sDays !== null && sDays > 7) return null;
                            return (
                                <ShareExtendRequest
                                    userThematiqueId={scopeInfo.thematique_id}
                                    ownerName={shared_by?.prenom || shared_by?.email}
                                    accessEnd={access_end}
                                />
                            );
                        })()}
                        {!user && (
                            <>
                                {scopeInfo?.free_until && (
                                    <span className="sl-badge-active">
                                        🎁 Gratuit jusqu'au <strong>{new Date(scopeInfo.free_until + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                                    </span>
                                )}
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowPricingPreview(true)}
                                >
                                    Tarifs
                                </button>
                            </>
                        )}
                    </div>
                    {user && (
                        <div className="sl-inline-hist">
                            <button
                                className={activeTab !== 'resultats' ? 'sl-inline-hist--active' : ''}
                                onClick={() => { setActiveTab('quiz'); if (isCompte) setTabForScope(scope, 'quiz'); }}
                            >
                                S’entraîner
                            </button>
                            <button
                                className={activeTab === 'resultats' ? 'sl-inline-hist--active' : ''}
                                onClick={() => { setActiveTab('resultats'); if (isCompte) setTabForScope(scope, 'resultats'); }}
                            >
                                Mes résultats
                            </button>
                        </div>
                    )}
                </div>
            </div>
            );
            })()}

            {/* Encart parrainage : thématiques éditoriales et communautaires, jamais perso (pas de
                confusion avec le partage des thématiques perso), masquable. */}
            {/* Notions à choisir (abonnement 6/12 mois ou pack) : rappel en tête, prioritaire sur le parrainage,
                pour qu'on n'ait pas besoin du mail d'annonce pour les trouver (25/10/2026). */}
            {!isHub && user && !guidedOpen && !isMaThematique && !isReadOnly && notionsCredits > 0 && activeTab !== 'notions' && (
                <NotionsAChoisirEncart offerCredits={notionsData.offerCredits || 0} packCredits={notionsData.packCredits || 0}
                    onOpen={() => { setNotionsFocus(true); setActiveTab('notions'); if (isCompte) setTabForScope(scope, 'notions'); }} />
            )}
            {!isHub && user && !guidedOpen && !isMaThematique && !isReadOnly && !(notionsCredits > 0 && activeTab !== 'notions') && (
                <ParrainageEncart scopeSlug={scope} parcours={parcoursMemorise} partner={isPartnerScope} />
            )}

            {/* === Hub mode: user thématique content (upload, tabs, etc.) === */}
            {/* Hub content is rendered by MesThematiquesHub above (includes nav + header slot + content) */}

            {!isHub && user && guidedOpen && activeTab !== 'resultats' && (has_access || !isMaThematique) && (
                <GuidedLearning key={`${scope}:${parcoursActif}`} embedded scopeSlugProp={scope} parcoursSlugProp={parcoursActif} autoLaunch={guidedAuto}
                    onAutoLaunched={() => setGuidedAuto(false)}
                    onChooseParcours={(slug) => setParcoursForScope(scope, slug)}
                    onSubscribe={scopeInfo?.id ? (source) => setSubModalInfo({ scopeId: scopeInfo.id, label: scopeInfo.label, extraBody: {}, source }) : null}
                    verdictUpsell={verdictUpsell}
                    onCloseEmbedded={() => { setGuidedOpen(false); setGuidedAuto(false); closeQuizModal(); }} />
            )}

            {/* Decision mode dock — hidden in hub mode and history mode */}
            {/* Reprise après absence (« Bon retour ») : en-tête et raccourcis intégrés au dock */}
            {!isHub && !guidedOpen && activeTab !== 'resultats' && (
                <WelcomeBackGate key={`${scope}:${parcoursActif}`} scopeId={user && engineAccess && scopeInfo?.id ? scopeInfo.id : null}>{wb => {
                const wbActive = !!wb.info && !smartQuizActive && !errorQuizActive;
                const wbShortcuts = wbActive
                    ? <WelcomeBackShortcuts info={wb.info} dismiss={wb.dismiss} onSm2={launchWbSm2} onErrors={launchWbErrors} />
                    : null;
                return (
                <ArcNav
                    activeTab={activeTab}
                    onTabChange={(tabId) => {
                        setActiveTab(tabId);
                        if (isCompte) setTabForScope(scope, tabId);
                    }}
                    scopeColor={scopeInfo?.couleur_theme}
                    scopeId={scopeInfo?.id}
                    categories={categories}
                    examenNbQuestions={examenNbQuestions}
                    hasUser={!!user}
                    notionsCount={notionsVisibles.length}
                    notionsNew={notionsVisibles.length > 0 && !notionsData.preview}
                    greeting={wbActive ? welcomeHeading(wb.info, user.prenom, wb.dismiss) : null}
                    extra={wbShortcuts}
                    nextStep={user && (has_access || !isMaThematique) ? (
                        <GuidedNextStep key={`${scope}:${parcoursActif}`} scopeSlug={scope} parcoursSlug={parcoursActif || ''} demo={!has_access}
                            extra={wbShortcuts}
                            onExpress={wbActive ? null : launchSm2Express}
                            onLaunch={() => { if (wbActive) { pingFeature('welcome_back_guided'); wb.dismiss(); } setGuidedAuto(true); setGuidedOpen(true); }}
                            onOpen={() => { setGuidedAuto(false); setGuidedOpen(true); }}
                            onChooseParcours={parcoursList.length >= 2 ? (slug) => setParcoursForScope(scope, slug) : null} />
                    ) : (!user && !isMaThematique ? (
                        // Visiteur : montrer que tout commence par un bilan (le plan n'existe qu'une fois inscrit).
                        <GuidedNextStepTeaser free={!!scopeInfo?.free_until && scopeInfo.free_until >= new Date().toISOString().slice(0, 10)} />
                    ) : null)}
                />
                );
                }}</WelcomeBackGate>
            )}

            {/* Everything below is hidden in hub mode */}
            {!isHub && <>

            {/* Upload retiré d'ici — déplacé dans TabQuiz via renderGestionSujets (section "Gestion des sujets") */}

            {/* Tab content */}
            {!guidedOpen && <div className="sl-content">
                {activeTab === 'quiz' && (
                    <TabQuiz
                        categories={categories}
                        has_access={has_access}
                        user={user}
                        openCategories={openCategories}
                        toggleCategory={toggleCategory}
                        onStart={handleStartSousSujet}
                        onDirectLaunch={directLaunch}
                        onLocked={() => setShowSubWall('sujet_verrouille')}
                        isCompte={isCompte}
                        scope={scope}
                        ignoredIds={ignoredIds}
                        onToggleIgnore={handleToggleIgnore}
                        isMaThematique={isMaThematique}
                        isReadOnly={isReadOnly}
                        filterFreeOnly={filterFreeOnly}
                        thematiqueId={scopeInfo?.thematique_id}
                        onRefresh={fetchData}
                        lastSousSujetId={prefs.lastSousSujets?.[scope] ?? null}
                        onResume={handleStartSousSujet}
                        showObsolete={showObsolete}
                        onToggleShowObsolete={() => setShowObsolete(v => !v)}
                        accentColor={scopeInfo?.couleur_theme}
                    />
                )}
                {activeTab === 'notions' && notionsData.notions.length > 0 && (
                    <TabNotions scopeInfo={scopeInfo} scopeSlug={scope} notions={notionsVisibles} previewMode={notionsData.preview} offerCredits={notionsData.offerCredits || 0} packCredits={notionsData.packCredits || 0} packs={notionsData.packs || []} onRefresh={loadNotions} focusHead={notionsFocus} />
                )}
                {activeTab === 'redaction' && (
                    <TabRedaction
                        scope={scope}
                        user={user}
                        has_access={has_access}
                        categories={categories}
                        accentColor={scopeInfo?.couleur_theme}
                        onLocked={() => setShowSubWall('redaction')}
                        onShowResults={() => { setActiveTab('resultats'); if (isCompte) setTabForScope(scope, 'resultats'); }}
                        onGoToSujet={(ssId) => {
                            // « Pour progresser » : ouvre l'onglet Quiz sur le sujet lié (catégorie dépliée, carte à l'écran)
                            setActiveTab('quiz');
                            if (isCompte) setTabForScope(scope, 'quiz');
                            const cat = (categories || []).find(c => (c.sous_sujets || []).some(s => s.id === ssId));
                            if (cat && !openCategories[cat.id]) toggleCategory(cat.id);
                            setTimeout(() => document.querySelector(`[data-ss-id="${ssId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
                        }}
                    />
                )}
                {activeTab === 'examen' && (
                    !user ? (
                        <div className="sl-login-prompt">
                            <p>Créez un compte gratuit pour accéder aux quiz mélangés et tester vos connaissances sur l'ensemble de la thématique.</p>
                            <Link to="/inscription" className="btn btn-primary">Créer mon compte gratuit</Link>
                            <Link to="/connexion" className="btn btn-secondary">Se connecter</Link>
                        </div>
                    ) : (
                    <TabExamen
                        scopeInfo={{ ...scopeInfo, image: scopeInfo?.image || (allScopes.find(s => s.slug === 'mes-thematiques')?.image || null) }}
                        categories={categories}
                        has_access={has_access}
                        user={user}
                        examenNbQuestions={examenNbQuestions}
                        setExamenNbQuestions={setExamenNbQuestions}
                        examenCategories={examenCategories}
                        setExamenCategories={setExamenCategories}
                        examenDiffMin={examenDiffMin}
                        setExamenDiffMin={setExamenDiffMin}
                        examenDiffMax={examenDiffMax}
                        setExamenDiffMax={setExamenDiffMax}
                        examenLoading={examenLoading}
                        setExamenLoading={setExamenLoading}
                        examenActive={examenActive}
                        setExamenActive={setExamenActive}
                        requireAccess={requireAccess}
                        quizOverlayBg={quizOverlayBg}
                        onQuizDone={() => { setHistLoaded(false); setStatsLoaded(false); }}
                        verdictAction={verdictAction}
                        verdictUpsell={verdictUpsell}
                        parcours={parcoursActif ? parcoursList.find(p => p.slug === parcoursActif) || null : null}
                    />
                    )
                )}
                {activeTab === 'entrainement' && (
                    !user ? (
                        <div className="sl-login-prompt">
                            <p>Créez un compte gratuit pour accéder à la progression intelligente, cibler vos faiblesses et consolider vos acquis.</p>
                            <Link to="/inscription" className="btn btn-primary">Créer mon compte gratuit</Link>
                            <Link to="/connexion" className="btn btn-secondary">Se connecter</Link>
                        </div>
                    ) : (
                    <TabEntrainement
                        scopeInfo={{ ...scopeInfo, image: scopeInfo?.image || (allScopes.find(s => s.slug === 'mes-thematiques')?.image || null) }}
                        parcoursActif={parcoursActif}
                        categories={categories}
                        user={user}
                        has_access={has_access}
                        engineAccess={engineAccess}
                        requireEngine={requireEngine}
                        masteryLoading={masteryLoading}
                        masteryData={masteryData}
                        smartQuizLoading={smartQuizLoading}
                        setSmartQuizLoading={setSmartQuizLoading}
                        smartQuizActive={smartQuizActive}
                        setSmartQuizActive={setSmartQuizActive}
                        onQuizDone={() => { setMasteryLoaded(false); setHistLoaded(false); setStatsLoaded(false); }}
                        verdictAction={verdictAction}
                        verdictUpsell={verdictUpsell}
                        errorMode={errorMode}
                        setErrorMode={setErrorMode}
                        errorCatId={errorCatId}
                        setErrorCatId={setErrorCatId}
                        errorLoading={errorLoading}
                        errorSearchDone={errorSearchDone}
                        setErrorSearchDone={setErrorSearchDone}
                        errorSujets={errorSujets} errorPlayable={errorPlayable}
                        errorTotal={errorTotal}
                        errorPages={errorPages}
                        errorPage={errorPage}
                        errorQuizActive={errorQuizActive}
                        setErrorQuizActive={setErrorQuizActive}
                        errorQuizLoading={errorQuizLoading}
                        setErrorQuizLoading={setErrorQuizLoading}
                        requireAccess={requireAccess}
                        searchErrors={searchErrors}
                        quizOverlayBg={quizOverlayBg}
                        onGoToQuiz={() => setActiveTab('quiz')}
                    />
                    )
                )}
                {activeTab === 'resultats' && (
                    !user ? (
                        <div className="sl-login-prompt">
                            <p>Créez un compte gratuit pour suivre votre progression, consulter votre historique et analyser vos statistiques.</p>
                            <Link to="/inscription" className="btn btn-primary">Créer mon compte gratuit</Link>
                            <Link to="/connexion" className="btn btn-secondary">Se connecter</Link>
                        </div>
                    ) : (!resultsAccess && !isReadOnly && histLoaded) ? (
                        // Sans abonnement ET sans historique. Deux situations tres
                        // differentes qu'on ne doit surtout pas confondre :
                        //  - `had_access` : l'abonnement a expire -> proposer de le reprendre ;
                        //  - sinon : compte gratuit qui n'a encore rien fait -> l'envoyer
                        //    vers les sujets d'essai, PAS vers un mur de paiement.
                        // Des qu'une personne a repondu a une seule question (sujets
                        // d'essai gratuits inclus), `engineAccess` passe a true et elle
                        // voit ses resultats : c'est precisement ce qu'on veut lui montrer.
                        <div className="sl-login-prompt">
                            <AlertTriangle size={32} style={{ color: '#f59e0b', marginBottom: 12 }} />
                            <p style={{ fontWeight: 600, color: 'var(--heading)', marginBottom: 4 }}>
                                {had_access ? 'Abonnement expir\u00e9' : 'Pas encore de r\u00e9sultats'}
                            </p>
                            <p style={{ marginBottom: 16 }}>
                                {had_access
                                    ? (isMaThematique
                                        ? 'Vos r\u00e9sultats et le suivi des participants de cette th\u00e9matique sont accessibles uniquement avec un abonnement actif.'
                                        : 'Reprenez votre abonnement pour retrouver votre historique et vos statistiques sur cette th\u00e9matique.')
                                    : 'Faites un premier quiz \u2014 les sujets d\u2019essai sont gratuits \u2014 et votre historique, vos statistiques et votre progression appara\u00eetront ici.'}
                            </p>
                            {had_access ? (
                                isMaThematique ? (
                                    <Link to="/compte/quiz/mes-thematiques" className="btn btn-primary">
                                        <Sparkles size={16} /> R&eacute;activer ma th&eacute;matique
                                    </Link>
                                ) : (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setSubModalInfo({ scopeId: scopeInfo.id, label: scopeInfo.label, extraBody: scopeInfo.thematique_id ? { user_thematique_id: scopeInfo.thematique_id } : {}, source: 'resultats' })}
                                    >
                                        <Sparkles size={16} /> Voir les tarifs
                                    </button>
                                )
                            ) : (
                                <button className="btn btn-primary" onClick={() => setActiveTab('quiz')}>
                                    <Sparkles size={16} /> Commencer un quiz gratuit
                                </button>
                            )}
                        </div>
                    ) : (
                    <TabResultats
                        scopeInfo={scopeInfo}
                        user={user}
                        histLoading={histLoading}
                        histEntries={histEntries}
                        statsLoading={statsLoading}
                        statsData={statsData}
                        resultatsSub={resultatsSub}
                        setResultatsSub={setResultatsSub}
                        startReview={startReview}
                        setModalSousSujetId={setModalSousSujetId}
                        setModalMode={setModalMode}
                        setModalNbQuestions={setModalNbQuestions}
                        setModalQuestionIds={setModalQuestionIds}
                        setModalOrigin={setModalOrigin}
                        onGoToQuiz={() => setActiveTab('quiz')}
                        onHistoryCleared={(!is_shared && !formateur_tracked) ? () => { setHistLoaded(false); setStatsLoaded(false); fetchData(); } : undefined}
                        showGenerations={false}
                        isOwnerThematique={isMaThematique && !isReadOnly}
                        thematiqueId={data?.scope?.thematique_id}
                    />
                    )
                )}
            </div>}

            </>}
            {/* End of !isHub wrapper */}

            {/* Picker unifié : nb questions + mode + lancer */}
            {launchPicker && (
                <div className="mode-picker-overlay" onClick={() => setLaunchPicker(null)}>
                    <div className="mode-picker" onClick={(e) => e.stopPropagation()}>
                        <h3 className="mode-picker-title">Configurer le quiz</h3>

                        {launchPicker.nbMax > 10 && (
                            <div className="mode-picker-section">
                                <span className="mode-picker-label">Nombre de questions</span>
                                <div className="mode-picker-options-row">
                                    {NB_QUESTIONS_OPTIONS.filter(n => n <= launchPicker.nbMax).map(n => (
                                        <button
                                            key={n}
                                            className={`sl-examen-option ${launchPicker.nbSelected === n ? 'active' : ''}`}
                                            onClick={() => setLaunchPicker(prev => ({ ...prev, nbSelected: n }))}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                    {!NB_QUESTIONS_OPTIONS.includes(launchPicker.nbMax) && (
                                        <button
                                            className={`sl-examen-option ${launchPicker.nbSelected === launchPicker.nbMax ? 'active' : ''}`}
                                            onClick={() => setLaunchPicker(prev => ({ ...prev, nbSelected: launchPicker.nbMax }))}
                                        >
                                            Toutes ({launchPicker.nbMax})
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mode-picker-section">
                            <span className="mode-picker-label">Mode</span>
                            <div className="mode-picker-options mode-picker-options--row">
                                <button
                                    className={`mode-option mode-option-apprentissage ${launchPicker.mode === 'apprentissage' ? 'selected' : ''}`}
                                    onClick={() => setLaunchPicker(prev => ({ ...prev, mode: 'apprentissage' }))}
                                >
                                    <span className="mode-option-icon"><BookOpen size={22} strokeWidth={1.8} /></span>
                                    <strong>Apprentissage</strong>
                                    <span className="mode-option-desc">Correction immédiate après chaque question.</span>
                                </button>
                                <button
                                    className={`mode-option mode-option-quiz ${launchPicker.mode === 'quiz' ? 'selected' : ''}`}
                                    onClick={() => setLaunchPicker(prev => ({ ...prev, mode: 'quiz' }))}
                                >
                                    <span className="mode-option-icon"><Clock size={22} strokeWidth={1.8} /></span>
                                    <strong>Classique</strong>
                                    <span className="mode-option-desc">Toutes les questions puis résultat global.</span>
                                </button>
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            style={{ marginTop: '1.5rem', width: '100%' }}
                            onClick={() => {
                                directLaunch(
                                    launchPicker.sousSujetId,
                                    launchPicker.mode,
                                    launchPicker.nbMax > 10 ? launchPicker.nbSelected : null
                                );
                                setLaunchPicker(null);
                            }}
                        >
                            Lancer le quiz ({launchPicker.nbMax > 10 ? launchPicker.nbSelected : launchPicker.nbMax} questions{(d => d ? ` · ${d}` : '')(estimateSessionDuration(launchPicker.secPerQ, launchPicker.mode, launchPicker.nbMax > 10 ? launchPicker.nbSelected : launchPicker.nbMax))})
                        </button>
                        <button className="mode-picker-cancel" onClick={() => setLaunchPicker(null)}>Annuler</button>
                    </div>
                </div>
            )}

            {/* Subscription Wall Popup */}
            {showSubWall && (
                <SubscriptionWall
                    scopeId={scopeInfo?.id}
                    scopeSlug={scope}
                    scopeLabel={scopeInfo?.label}
                    onClose={() => setShowSubWall(false)}
                    extraBody={scopeInfo?.thematique_id ? { user_thematique_id: scopeInfo.thematique_id } : {}}
                    pricing={scopeInfo}
                    source={typeof showSubWall === 'string' ? showSubWall : undefined}
                />
            )}

            {proposeAcces && !scopeInfo?.subscription_end && <UnlockFab prix={prixEntree} onClick={() => ouvrirAbonnement('bouton_flottant')} />}
            {subModalInfo && (
                <SubscriptionModal
                    scopeId={subModalInfo.scopeId}
                    scopeLabel={subModalInfo.label}
                    onClose={() => setSubModalInfo(null)}
                    extraBody={subModalInfo.extraBody}
                    pricing={scopeInfo}
                    currentAccessEnd={has_access ? access_end : null}
                    source={subModalInfo.source}
                />
            )}

            {/* Pricing preview for non-logged users */}
            {showPricingPreview && (
                <div className="checkout-modal-overlay" onClick={() => setShowPricingPreview(false)}>
                    <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="checkout-modal__header">
                            <span className="checkout-modal__title">{scopeInfo?.label || 'Abonnement'}</span>
                            <button className="checkout-modal__close" onClick={() => setShowPricingPreview(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="checkout-modal__body">
                            <div className="sub-modal-plans">
                                <div className="sub-modal-grid">
                                    {[
                                        { label: '1 mois', prix: scopeInfo?.price_1m || 3, mois: 1, duree: '1m' },
                                        { label: '6 mois', prix: scopeInfo?.price_6m || 10, mois: 6, popular: true, duree: '6m' },
                                        { label: '12 mois', prix: scopeInfo?.price_12m || 18, mois: 12, duree: '12m' },
                                    ].map((p) => {
                                        const total = parseFloat(p.prix);
                                        const perMonth = (total / p.mois).toFixed(2).replace('.', ',').replace(/,?0+$/, '');
                                        return (
                                            <div key={p.label} className={`sub-modal-plan ${p.popular ? 'sub-modal-plan--popular' : ''}`}>
                                                {p.popular && <span className="sub-modal-badge">Populaire</span>}
                                                <span className="sub-modal-duree">{p.label}</span>
                                                <span className="sub-modal-prix">{Number.isInteger(total) ? total : total.toFixed(2).replace('.', ',')}&euro;</span>
                                                <span className="sub-modal-mois">{perMonth}&euro;/mois</span>
                                                {scopeInfo?.qrc_quota?.[p.duree] > 0 && (
                                                    <span className="sub-modal-qrc" title="Corrections de réponses rédigées ajoutées à ton solde, sans date limite">
                                                        + {scopeInfo.qrc_quota[p.duree]} corrections rédigées
                                                    </span>
                                                )}
                                                {scopeInfo?.notions_offertes?.[p.duree] > 0 && (
                                                    <span className="sub-modal-qrc" title="Notions à la carte (fiche + QCM) à choisir dans cette thématique, acquises définitivement">
                                                        + {scopeInfo.notions_offertes[p.duree]} notion{scopeInfo.notions_offertes[p.duree] > 1 ? 's' : ''} offerte{scopeInfo.notions_offertes[p.duree] > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <Link to="/inscription" className="btn btn-primary sub-modal-cta" onClick={() => setShowPricingPreview(false)}>
                                    Créer mon compte gratuit
                                </Link>
                                <Link to="/connexion" className="btn btn-secondary sub-modal-cta" style={{ marginTop: '8px' }} onClick={() => setShowPricingPreview(false)}>
                                    Se connecter
                                </Link>
                                <p className="sub-modal-note">Paiement unique, sans renouvellement automatique.</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Quiz Modal */}
            {modalSousSujetId && (
                <div className={`quiz-modal-overlay ${quizOverlayBg.backgroundImage ? 'has-bg-image' : ''}`} style={quizOverlayBg}>
                    <Quiz
                        sousSujetIdProp={modalSousSujetId}
                        scopeIdProp={scopeInfo?.id}
                        scopeInfoProp={{ ...scopeInfo, image: scopeInfo?.image || (allScopes.find(s => s.slug === 'mes-thematiques')?.image || null) }}
                        nbQuestionsProp={modalNbQuestions}
                        modeProp={modalMode}
                        questionIdsProp={modalQuestionIds}
                        originProp={modalOrigin}
                        isCustomProp={isMaThematique}
                        isPartnerProp={isPartnerScope}
                        verdictAction={verdictAction}
                        verdictUpsell={verdictUpsell}
                        onClose={closeQuizModal}
                    />
                </div>
            )}
        </div>
    );
}
